/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { TrackingConsent } from '@/types/tracking-consent';
import { KETCH_ESSENTIAL_PURPOSE_CODES } from './constants';
import type { KetchConsent } from './types';

/**
 * Normalize the Ketch `consent` event argument into a purpose → granted map.
 * The tag sometimes emits `{ purposes: { ... } }` and sometimes the map itself.
 */
export function getKetchPurposes(consent: KetchConsent | Record<string, boolean> | undefined): Record<string, boolean> {
    if (!consent || typeof consent !== 'object') {
        return {};
    }

    if ('purposes' in consent && consent.purposes && typeof consent.purposes === 'object') {
        return consent.purposes;
    }

    return consent as Record<string, boolean>;
}

/**
 * Map Ketch purpose grants onto the storefront's binary SLAS tracking consent.
 *
 * - If `trackingPurposes` is set, any listed purpose that is `true` → Accepted.
 * - Otherwise any granted non-essential purpose → Accepted.
 * - Essential-only (or empty) consent → Declined, so Einstein/Active Data stay dark until opt-in.
 */
export function ketchConsentToTrackingConsent(
    consent: KetchConsent | Record<string, boolean> | undefined,
    trackingPurposes?: string[]
): TrackingConsent {
    const purposes = getKetchPurposes(consent);

    if (trackingPurposes && trackingPurposes.length > 0) {
        return trackingPurposes.some((code) => purposes[code] === true)
            ? TrackingConsent.Accepted
            : TrackingConsent.Declined;
    }

    const grantedNonEssential = Object.entries(purposes).some(
        ([code, granted]) => granted === true && !KETCH_ESSENTIAL_PURPOSE_CODES.has(code)
    );

    return grantedNonEssential ? TrackingConsent.Accepted : TrackingConsent.Declined;
}
